<script setup>
import { createReusableTemplate, useIntervalFn } from '@vueuse/core'
import dayjs from 'dayjs'
import { cloneDeep, isEmpty, isEqual, isNull, isUndefined, pick } from 'lodash'
import { computed, reactive, ref, shallowRef, toValue, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import CheckinApiHandler from '@/api/check-in'
import { tracker } from '@/tracking/amplitude'
import { ELEMENT_TYPES_FOR_TRACKING } from '@/tracking/amplitude-helpers'
import { EVENT_NAMES } from '@/tracking/gtm-helpers'
import { gtmTracker } from '@/tracking/gtm-tracking'
import { useBackDate } from '@/utils/backdate'
import {
  CHECKIN_DRAFT_MAP_LS_KEY,
  CHECKIN_DRAFT_MAP_SIZE_LIMIT,
  CHECKIN_FIELDS_KEYS,
  CHECKIN_MODAL_ENTRY_POINTS,
  CHECKIN_PREVIEW_NAVIGATION_BUTTONS,
  CHECKINS_PREVIEW_LIMIT,
  getCheckinDraftItemLsKey,
  isCheckinHistoricValueExist,
  updateCheckinCommentReactions,
  updateCheckinReplies,
  useCheckinModal,
  useCheckinValueField,
  useCloseElement
} from '@/utils/check-ins/check-ins'
import { APP_MODAL_SIZES } from '@/utils/components-configurations/app-modal'
import { getStartOfTheDay, UNSELECTED_DATE } from '@/utils/date'
import { formatDate } from '@/utils/date'
import { resolveBinaryProgressValue, useInlineUpdate } from '@/utils/element-progress'
import { CHECKIN_ENTITY_KEYS, COMMENT_ENTITY_KEYS, REQUEST_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { createHtmlElementId, isStringEmpty } from '@/utils/general'
import { showNotify } from '@/utils/notify'
import { isKR } from '@/utils/objective-types'
import { isOkrElementClosed, OKR_STATUSES } from '@/utils/objectives'
import { isBinaryProgress, METRIC_VALUES_KEYS } from '@/utils/okr-element-values'
import {
  deleteStorageByKey,
  isLocalStorageAvailable,
  readFromStorageByKey,
  updateStorageByKey
} from '@/utils/persist'
import { includesValue, numberOrNullProp, objectOrNullProp } from '@/utils/prop-validators'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import AppDialog from '@/components/AppDialog'
import AppDroplist from '@/components/AppDroplist'
import {
  ELEMENT_PROGRESS_MOVE_BACKWARD_BUTTON_TEST_ID,
  ELEMENT_PROGRESS_MOVE_FORWARD_BUTTON_TEST_ID,
  SHOW_LATEST_CHECKIN_TRIGGER_BUTTON_TEST_ID
} from '@/components/check-ins/jest-helpers'
import CheckinForm from '@/components/objectives/element-check-in/CheckinForm'
import CheckinPreview from '@/components/objectives/element-check-in/CheckinPreview'
import ModalFooterActions from '@/components/objectives/forms/ModalFooterActions'
import OkrIcon from '@/components/objectives/items/OkrIcon'
import TitleItems from '@/components/objectives/items/TitleItems'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppTitle from '@/components/ui/AppTitle/AppTitle'

defineOptions({
  name: 'CheckinModal'
})

const props = defineProps({
  entryPoint: {
    type: String,
    validator: v => includesValue(v, CHECKIN_MODAL_ENTRY_POINTS),
    required: true
  },

  okrElement: {
    type: Object,
    required: true
  },

  presetBackdate: {
    type: [String, Date, Number],
    default: null
  },

  overrideCurrentValue: {
    validator: v => numberOrNullProp(v) || isUndefined(v),
    default: null
  },

  trackingSource: {
    type: String,
    default: 'unknown'
  },

  viewConfig: {
    validator: v => objectOrNullProp(v),
    default: null
  }
})

const {
  ID,
  IS_STARTED,
  LOCAL_CONFIDENCE_LEVEL_ID,
  LOCAL_AUTOMATIC_CONFIDENCE_LEVEL_ID,
  LOCAL_CURRENT_VALUE,
  LOCAL_GRADE_VALUE,
  BACKDATE,
  COMMENT,
  CLOSE_NESTED_ELEMENTS,
  FROM,
  TO,
  VALUE,
  COMMENTS,
  NEW_STATUS,
  NEW_VALUE,
  CHECKIN_DATE,
  UPDATE_STATUS_FOR_NESTED_ELEMENTS
} = CHECKIN_ENTITY_KEYS

const { AUTOMATIC_CONFIDENCE_LEVEL_ID, GRADE_TO_USE, CONFIDENCE_LEVEL_ID } = OKR_ELEMENT_KEYS

const { CURRENT_VALUE } = METRIC_VALUES_KEYS

const { VALUE_CHANGE, STATUS_CHANGE, PROGRESS_CHANGE } = CHECKIN_FIELDS_KEYS

const { t } = useI18n()

const emit = defineEmits({
  saved: null,
  'after-leave': null
})

const isLaunchAsReopen = computed(() => {
  return isOkrElementClosed(props.okrElement)
})

const { canCreateCheckinToElements, isLaunchAsAnyPreviewMode, isLaunchAsPreviewOnly } =
  useCheckinModal({
    okrElement: () => props.okrElement,
    entryPoint: () => props.entryPoint
  })

const onBeforeEnter = () => {
  if (isLaunchAsAnyPreviewMode.value) {
    getLatestCheckins()
  } else {
    startNewCheckin()
  }
}

const onAfterLeave = () => {
  emit('after-leave')
}

const isOpen = defineModel({
  type: Boolean,
  required: true,
  set: v => v
})

const onCancel = () => {
  if (isShowCheckinPreviewDropdown.value) return

  if (isEdit.value) {
    const index = latestCheckinsListState.value.items.findIndex(
      item => item[ID] === checkinToEdit.value[ID]
    )

    latestCheckinsListState.value[INITIAL_SLIDE_INDEX] =
      index >= DEFAULT_LATEST_CHECKINS_LIST_STATE[INITIAL_SLIDE_INDEX]
        ? index
        : DEFAULT_LATEST_CHECKINS_LIST_STATE[INITIAL_SLIDE_INDEX]
    resetCheckinModel()

    return
  }

  onClose()
}

const onClose = () => {
  if (isShowCheckinPreviewDropdown.value) return

  if (draftInterval || commentWatcher) {
    setDraft()
  }

  isOpen.value = false
  revertToInitialState()
}

const resetCheckinModel = () => {
  checkinModel.value = cloneDeep(CHECKIN_DEFAULT_MODEL)
  checkinModelSnapshot.value = null
  checkinToEdit.value = null
}

const revertToInitialState = () => {
  isCommentTooLong.value = false
  resetCheckinModel()

  latestCheckinsListState.value = cloneDeep(DEFAULT_LATEST_CHECKINS_LIST_STATE)

  destroyCloseElementState()

  // NOTE: clear draft state since it's no longer needed
  clearDraft()
  clearCommentWatcher()
}
const isProgressLoading = ref(false)

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ CHECKIN MODEL SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const CHECKIN_DEFAULT_MODEL = {
  [IS_STARTED]: false,
  [LOCAL_CONFIDENCE_LEVEL_ID]: OKR_STATUSES.AUTO,
  [LOCAL_AUTOMATIC_CONFIDENCE_LEVEL_ID]: OKR_STATUSES.AUTO,
  [LOCAL_CURRENT_VALUE]: null,
  [LOCAL_GRADE_VALUE]: null,
  [BACKDATE]: UNSELECTED_DATE,
  [COMMENT]: '',
  [CLOSE_NESTED_ELEMENTS]: false
}

const checkinModel = ref(cloneDeep(CHECKIN_DEFAULT_MODEL))

const checkinModelSnapshot = shallowRef(null)

const checkinToEdit = shallowRef(null)

const isEdit = computed(() => {
  return !isEmpty(checkinToEdit.value)
})

const getHistoricValue = ({ parameter = {}, path = 'unknown' }) => {
  const isHistoricValueExist = isCheckinHistoricValueExist({
    parameter,
    path
  })

  return isHistoricValueExist ? parameter[path][VALUE] : null
}

const startNewCheckin = () => {
  checkinModel.value[IS_STARTED] = true
  applyBackdate()

  const { okrElement } = props

  const checkinToEditData = toValue(checkinToEdit)

  let localGradeValue = okrElement[GRADE_TO_USE]
  let localCurrentValue = isBinary.value
    ? resolveBinaryProgressValue(okrElement[CURRENT_VALUE])
    : okrElement[CURRENT_VALUE] ?? null
  let localConfidenceLevelId = okrElement[CONFIDENCE_LEVEL_ID] || OKR_STATUSES.AUTO
  let localAutomaticConfidenceLevelId =
    okrElement[AUTOMATIC_CONFIDENCE_LEVEL_ID] || OKR_STATUSES.AUTO
  let comment = CHECKIN_DEFAULT_MODEL[COMMENT]

  if (isEdit.value) {
    const progressTo = getHistoricValue({
      parameter: checkinToEditData[PROGRESS_CHANGE],
      path: TO
    })

    const progressFrom = getHistoricValue({
      parameter: checkinToEditData[PROGRESS_CHANGE],
      path: FROM
    })

    localGradeValue = progressTo ?? progressFrom ?? 0

    const valueTo = getHistoricValue({
      parameter: checkinToEditData[VALUE_CHANGE],
      path: TO
    })

    const valueFrom = getHistoricValue({
      parameter: checkinToEditData[VALUE_CHANGE],
      path: FROM
    })

    let currentValue = valueTo ?? valueFrom

    localCurrentValue = isBinary.value ? resolveBinaryProgressValue(currentValue) : currentValue

    localConfidenceLevelId = checkinToEditData[STATUS_CHANGE][TO][VALUE]
    localAutomaticConfidenceLevelId =
      checkinToEditData[STATUS_CHANGE][TO][AUTOMATIC_CONFIDENCE_LEVEL_ID]

    if (!isEmpty(checkinToEditData[COMMENTS])) {
      comment =
        checkinToEditData[COMMENTS][0][COMMENT_ENTITY_KEYS.TEXT] || CHECKIN_DEFAULT_MODEL[COMMENT]
    }
  }

  checkinModel.value = {
    ...checkinModel.value,
    localGradeValue,
    localCurrentValue,
    localConfidenceLevelId,
    localAutomaticConfidenceLevelId,
    comment
  }

  checkinModelSnapshot.value = cloneDeep(checkinModel.value)

  if (!isEdit.value && props.okrElement[OKR_ELEMENT_KEYS.LAST_CHECKIN]) {
    getLatestCheckins()
  }

  if (shouldApplyDraft.value) {
    if (!isLocalStorageAvailable()) return

    const success = initFromDraft()
    if (success) {
      initDraftWatcher()
    } else {
      initCommentWatcher()
    }
  }
}

const shouldApplyDraft = computed(() => !isEdit.value && !isLaunchAsReopen.value)

const startEditCheckin = checkin => {
  checkinToEdit.value = cloneDeep(checkin)
  startNewCheckin()
}

const LATEST_CHECKINS_LIST_STATE_KEYS = {
  OFFSET: 'offset',
  IS_ALL_ITEMS_LOADED: 'isAllItemsLoaded',
  ITEMS: 'items',
  IS_LOADING: 'isLoading',
  INITIAL_SLIDE_INDEX: 'initialSlideIndex'
}

const { OFFSET, IS_ALL_ITEMS_LOADED, ITEMS, IS_LOADING, INITIAL_SLIDE_INDEX } =
  LATEST_CHECKINS_LIST_STATE_KEYS

const DEFAULT_LATEST_CHECKINS_LIST_STATE = {
  [OFFSET]: 0,
  [IS_ALL_ITEMS_LOADED]: false,
  [ITEMS]: [],
  [IS_LOADING]: false,
  [INITIAL_SLIDE_INDEX]: 0
}

const latestCheckinsListState = ref(cloneDeep(DEFAULT_LATEST_CHECKINS_LIST_STATE))

const resetInitialSlideIndex = () => {
  if (latestCheckinsListState.value[INITIAL_SLIDE_INDEX]) {
    latestCheckinsListState.value[INITIAL_SLIDE_INDEX] =
      DEFAULT_LATEST_CHECKINS_LIST_STATE[INITIAL_SLIDE_INDEX]
  }
}

const checkinsForPreview = computed(() => {
  if (isEmpty(latestCheckinsListState.value[ITEMS])) {
    return []
  }

  if (checkinModel.value[IS_STARTED]) {
    return latestCheckinsListState.value[ITEMS].slice(0, 1)
  }

  return latestCheckinsListState.value[ITEMS]
})

const loadMoreCheckins = async () => {
  if (
    latestCheckinsListState.value[IS_LOADING] ||
    latestCheckinsListState.value[IS_ALL_ITEMS_LOADED]
  ) {
    return
  }

  latestCheckinsListState.value[OFFSET] += CHECKINS_PREVIEW_LIMIT

  await getLatestCheckins()
}

const getLatestCheckins = async ({ reloadAll = false } = {}) => {
  const api = new CheckinApiHandler()

  latestCheckinsListState.value[IS_LOADING] = true

  const offset = reloadAll ? 0 : latestCheckinsListState.value[OFFSET]
  const limit = reloadAll
    ? Math.max(CHECKINS_PREVIEW_LIMIT, latestCheckinsListState.value.items.length)
    : CHECKINS_PREVIEW_LIMIT

  try {
    const result = await api.getElementCheckins({
      elementId: props.okrElement[OKR_ELEMENT_KEYS.ID],
      offset,
      limit
    })

    if (result.length < limit && !reloadAll) {
      // set isAllItemsLoaded to true only if we are not reloading all items
      latestCheckinsListState.value[IS_ALL_ITEMS_LOADED] = true
    }

    if (reloadAll) {
      latestCheckinsListState.value[ITEMS] = [...result]
    } else {
      latestCheckinsListState.value[ITEMS].push(...result)
    }
  } catch (error) {
    handleError({ error })
  } finally {
    latestCheckinsListState.value[IS_LOADING] = false

    if (isEmpty(latestCheckinsListState.value[ITEMS]) && isLaunchAsAnyPreviewMode.value) {
      onClose()
    }
  }
}

const latestCheckinNavigation = computed(() => {
  if (latestCheckinsListState.value[ITEMS].length < 2 || checkinModel.value[IS_STARTED]) {
    return []
  }

  return Object.values(CHECKIN_PREVIEW_NAVIGATION_BUTTONS).map(button => {
    const isNextButton = button === CHECKIN_PREVIEW_NAVIGATION_BUTTONS.nextEl

    return {
      id: button,
      class: button.slice(1),
      testId: isNextButton
        ? ELEMENT_PROGRESS_MOVE_BACKWARD_BUTTON_TEST_ID
        : ELEMENT_PROGRESS_MOVE_FORWARD_BUTTON_TEST_ID,
      icon: isNextButton ? 'chevron-left-next' : 'chevron-right-next'
    }
  })
})

// const checkinToDelete = shallowRef(null)

// const confirmDeleteCheckinEntity = computed(() => {
//   if (!checkinToDelete.value) return ''
//
//   console.log(checkinToDelete.value)
//
//   const { name } = checkinToDelete.value.author || { name: 'Unknown' }
//
//   const date = dayjs(checkinToDelete.value.checkinDate).format(MILESTONE_DAY_FORMAT)
//
//   return `Check-in made by ${name} on ${date} i18n!`
// })

// const showDeleteCheckinModal = checkinData => {
//   checkinToDelete.value = checkinData
// }

// const closeDeleteCheckinModal = () => {
//   checkinToDelete.value = null
// }
//
// const isDeleteCheckinLoading = ref(false)

// const onDeleteCheckin = async () => {
//   if (!checkinToDelete.value) return
//
//   const api = new CheckinApiHandler()
//
//   isDeleteCheckinLoading.value = true
//
//   try {
//     await api.deleteCheckin({ id: checkinToDelete.value.id })
//     closeDeleteCheckinModal()
//     getLatestCheckins({ reloadAll: true })
//   } catch (error) {
//     handleError({ error })
//   } finally {
//     isDeleteCheckinLoading.value = false
//   }
// }

const isCreateOrUpdateCheckinLoading = ref(false)

const onConfirm = () => {
  if (isShowCheckinPreviewDropdown.value) return

  if (isEdit.value && checkinModel.value[IS_STARTED]) {
    onUpdateCheckin()
  } else if (isLaunchAsReopen.value && checkinModel.value[IS_STARTED]) {
    createReopenCheckin()
  } else if (checkinModel.value[IS_STARTED]) {
    onCreateCheckin()
  } else {
    startNewCheckin()
  }
}

const onUpdateCheckin = async () => {
  isCreateOrUpdateCheckinLoading.value = true

  const api = new CheckinApiHandler()

  try {
    const payload = {
      [ID]: checkinToEdit.value[ID],
      [NEW_STATUS]: checkinModel.value[LOCAL_CONFIDENCE_LEVEL_ID],
      [CHECKIN_DATE]: getBackdateAsRequestParameter.value,
      [COMMENT]: checkinModel.value[COMMENT],
      [REQUEST_ENTITY_KEYS.VIEW_CONFIG]: props.viewConfig
    }

    if (isValueFieldNeeded.value && !isValueFieldDisabledByDataSourceQuery.value) {
      payload[NEW_VALUE] = checkinModel.value[LOCAL_CURRENT_VALUE]
    }

    const result = await api.updateCheckin(payload)

    const { element, updatedElementParameters, changedElementDates, ...updatedCheckin } = result

    latestCheckinsListState.value[ITEMS] = latestCheckinsListState.value[ITEMS].map(item => {
      if (item[ID] === updatedCheckin[ID]) {
        return {
          ...item,
          ...updatedCheckin
        }
      }
      return item
    })

    showNotify({
      title: t('checkin.updated_message')
    })

    emit('saved', {
      elementUniqueId: props.okrElement[OKR_ELEMENT_KEYS.UNIQUE_ID],
      element,
      updatedElementParameters,
      changedElementDates
    })

    await getLatestCheckins({ reloadAll: true })

    const newCheckinIndex = latestCheckinsListState.value[ITEMS].findIndex(
      item => item[ID] === updatedCheckin[ID]
    )

    if (newCheckinIndex >= 0) {
      latestCheckinsListState.value[INITIAL_SLIDE_INDEX] = newCheckinIndex
      resetCheckinModel()
    } else {
      onClose()
    }
  } catch (error) {
    handleError({ error })
  } finally {
    isCreateOrUpdateCheckinLoading.value = false
  }
}

const logCreateCheckinEvent = payload => {
  gtmTracker.logEvent(EVENT_NAMES.CHECKIN_CREATED)

  const YES = 'yes'
  const NO = 'no'

  const eventPayload = {
    element_type: isKR(props.okrElement)
      ? ELEMENT_TYPES_FOR_TRACKING.METRIC
      : ELEMENT_TYPES_FOR_TRACKING.OBJECTIVE,
    element_id: props.okrElement[OKR_ELEMENT_KEYS.ID],
    comment_left: isStringEmpty(payload[COMMENT]) ? NO : YES,
    status_updated: props.okrElement[CONFIDENCE_LEVEL_ID] === payload[NEW_STATUS] ? NO : YES,
    source: props.trackingSource
  }

  if (isValueFieldNeeded.value && !isValueFieldDisabledByDataSourceQuery.value) {
    eventPayload.progress_updated =
      props.okrElement[CURRENT_VALUE] === payload[NEW_VALUE] ? NO : YES
  }

  tracker.logEvent('check-in completed', eventPayload)
}

const onCreateCheckin = async () => {
  isCreateOrUpdateCheckinLoading.value = true

  const api = new CheckinApiHandler()

  try {
    const payload = {
      elementId: props.okrElement[OKR_ELEMENT_KEYS.ID],
      [NEW_STATUS]: checkinModel.value[LOCAL_CONFIDENCE_LEVEL_ID],
      [CHECKIN_DATE]: getBackdateAsRequestParameter.value,
      [COMMENT]: checkinModel.value[COMMENT],
      [REQUEST_ENTITY_KEYS.VIEW_CONFIG]: props.viewConfig
    }

    if (isValueFieldNeeded.value && !isValueFieldDisabledByDataSourceQuery.value) {
      payload[NEW_VALUE] = checkinModel.value[LOCAL_CURRENT_VALUE]
    }

    if (isShowCloseNestedElementsCheckbox.value) {
      payload[UPDATE_STATUS_FOR_NESTED_ELEMENTS] = checkinModel.value[CLOSE_NESTED_ELEMENTS]
    }

    const { element, updatedElementParameters } = await api.createCheckin(payload)

    showNotify({
      title: t('checkin.created_message')
    })

    removeDraftFromStorage(payload.elementId)
    // NOTE: clear draft state since it's no longer needed and not to save anymore changes to storage after checkin is created
    clearDraft()
    clearCommentWatcher()

    logCreateCheckinEvent(payload)

    onClose()
    emit('saved', {
      elementUniqueId: props.okrElement[OKR_ELEMENT_KEYS.UNIQUE_ID],
      element,
      updatedElementParameters
    })
  } catch (error) {
    handleError({ error })
  } finally {
    isCreateOrUpdateCheckinLoading.value = false
  }
}

const createReopenCheckin = async () => {
  isCreateOrUpdateCheckinLoading.value = true

  const api = new CheckinApiHandler()

  try {
    const payload = {
      elementId: props.okrElement[OKR_ELEMENT_KEYS.ID],
      [NEW_STATUS]: OKR_STATUSES.AUTO,
      [CHECKIN_DATE]: UNSELECTED_DATE,
      [COMMENT]: checkinModel.value[COMMENT],
      [REQUEST_ENTITY_KEYS.VIEW_CONFIG]: props.viewConfig
    }

    const { element, updatedElementParameters } = await api.createCheckin(payload)

    showNotify({
      title: t('checkin.reopen_created.message')
    })

    logCreateCheckinEvent(payload)

    onClose()
    emit('saved', {
      elementUniqueId: props.okrElement[OKR_ELEMENT_KEYS.UNIQUE_ID],
      element,
      updatedElementParameters
    })
  } catch (error) {
    handleError({ error })
  } finally {
    isCreateOrUpdateCheckinLoading.value = false
  }
}

const checkinPreviewUid = useId()

const tableActionsAppendToSelectors = computed(() => {
  return createHtmlElementId({
    prefix: 'chm-content',
    uid: checkinPreviewUid
  })
})

const {
  onTriggerClick: onShowCheckinPreviewButtonClick,
  isShowInlineUpdateForm: isShowCheckinPreviewDropdown,
  editInitialised: checkinPreviewDropdownInitialised
} = useInlineUpdate({
  props: reactive({
    updatable: true
  })
})

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ CHECKIN MODEL SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ MODAL PARAMETERS SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮
const modalTitle = computed(() => {
  if (checkinModel.value[IS_STARTED]) {
    const keyPath = isEdit.value ? 'common.edit_entity' : 'common.new_entity'

    return t(keyPath, {
      entity: t('checkin').toLowerCase()
    })
  }

  return t('checkin.latest_checkin')
})

const confirmButtonText = computed(() => {
  if (isLaunchAsReopen.value) {
    return t('dashboard.reopen')
  }

  if (checkinModel.value[IS_STARTED]) {
    return t('action.submit')
  }

  return t('common.new_entity', {
    entity: t('checkin').toLowerCase()
  })
})

const { isValueFieldNeeded, isValueFieldDisabledByDataSourceQuery } = useCheckinValueField({
  okrElement: () => props.okrElement
})

const isBinary = computed(() => {
  return isBinaryProgress({
    element: props.okrElement
  })
})

const confirmButtonTooltip = computed(() => {
  if (!canCreateCheckinToElements.value) {
    const element = isKR(props.okrElement) ? t('okr_elements.metric') : t('okr_elements.objective')

    return t('lock_message', { element })
  }

  return null
})

const isConfirmButtonDisabled = computed(() => {
  if (isCommentTooLong.value) return true

  if (!canCreateCheckinToElements.value) return true

  if (!checkinModel.value[IS_STARTED]) return false

  if (isLaunchAsReopen.value) return false

  const isValueFieldEmpty =
    isNull(checkinModel.value[LOCAL_CURRENT_VALUE]) ||
    isStringEmpty(String(checkinModel.value[LOCAL_CURRENT_VALUE]))

  const isDisabledByValue = isValueFieldNeeded.value && !isBinary.value && isValueFieldEmpty

  if (isDisabledByValue && !isValueFieldDisabledByDataSourceQuery.value) {
    return true
  }

  const paramsToCompare = [
    COMMENT,
    LOCAL_CONFIDENCE_LEVEL_ID,
    LOCAL_AUTOMATIC_CONFIDENCE_LEVEL_ID,
    LOCAL_CURRENT_VALUE
  ]

  return isEqual(
    pick(checkinModel.value, paramsToCompare),
    pick(checkinModelSnapshot.value, paramsToCompare)
  )
})

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ MODAL PARAMETERS SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ GRADE AND STATUS SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const {
  isShowCloseNestedElementsCheckbox,
  handleCloseNestedElementsCheckbox,
  destroyCloseElementState
} = useCloseElement({
  localConfidenceLevelId: () => checkinModel.value[LOCAL_CONFIDENCE_LEVEL_ID],
  elementId: () => props.okrElement[OKR_ELEMENT_KEYS.ID],
  childCount: () => props.okrElement[OKR_ELEMENT_KEYS.CHILD_COUNT]
})

const onConfidenceLevelIdUpdated = async () => {
  isProgressLoading.value = true
  await handleCloseNestedElementsCheckbox()
  isProgressLoading.value = false
}

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ GRADE AND STATUS SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

// const getStatusOptionForDisplay = (option, basedOnGrade) => {
//   // it means that auto status mode selected
//   if (!isTodaySelected.value && basedOnGrade) {
//     return {
//       ...OBJECTIVE_STATUS_AUTO,
//       label: t(OBJECTIVE_STATUS_AUTO.label)
//     }
//   }
//
//   return option
// }

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ BACKDATE SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const { getBackdateAsRequestParameter, today } = useBackDate({
  selectedDate: () => checkinModel.value[BACKDATE]
})

const applyBackdate = () => {
  const presetBackdate = isEdit.value ? checkinToEdit.value[CHECKIN_DATE] : props.presetBackdate

  const resolvedBackdate = dayjs(presetBackdate).isValid()
    ? getStartOfTheDay(dayjs(presetBackdate))
    : UNSELECTED_DATE

  checkinModel.value.backdate =
    resolvedBackdate && Number(resolvedBackdate) < Number(today)
      ? resolvedBackdate
      : UNSELECTED_DATE
}
// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ BACKDATE SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

const isCommentTooLong = ref(false)

const onUpdateReactions = ({ checkinId, commentId, parentId, reactions }) => {
  updateCheckinCommentReactions({
    checkinsList: latestCheckinsListState.value[ITEMS],
    checkinId,
    commentId,
    parentId,
    reactions
  })
}

//#region DRAFT SCOPE
const draftSaved = ref(false)
const draftSavedDate = ref(null)

let draftInterval = null
let draftPayload = null
const initDraftWatcher = () => {
  if (draftInterval) draftInterval.pause()

  draftInterval = useIntervalFn(() => {
    setDraft()
  }, 3000)
}

const clearDraft = () => {
  draftSaved.value = false
  draftSavedDate.value = null
  draftPayload = null

  if (draftInterval) {
    draftInterval.pause()
    draftInterval = null
  }
}

const isOkToSave = (id, map) => {
  try {
    if (map[id] || Object.keys(map).length < CHECKIN_DRAFT_MAP_SIZE_LIMIT) return true

    const values = Object.entries(map).sort(
      ([, timestampA], [, timestampB]) => timestampB - timestampA
    )

    const [removedDraftId] = values.at(-1)
    removeDraftFromStorage(removedDraftId)

    return true
  } catch (error) {
    console.error('Error checking if it is ok to save the draft:', error)
    return false
  }
}

const removeDraftFromStorage = id => {
  try {
    const map = readFromStorageByKey(CHECKIN_DRAFT_MAP_LS_KEY) || {}
    delete map[id]
    updateStorageByKey(CHECKIN_DRAFT_MAP_LS_KEY, map)

    deleteStorageByKey(getCheckinDraftItemLsKey(id))
  } catch (error) {
    console.error('Error removing draft from storage:', error)
  }
}

const isEmptyDraft = payload => {
  return !payload[COMMENT]
}

const setDraft = async () => {
  try {
    if (!isLocalStorageAvailable()) return

    const itemId = props.okrElement[OKR_ELEMENT_KEYS.ID]
    const map = readFromStorageByKey(CHECKIN_DRAFT_MAP_LS_KEY) || {}
    if (!isOkToSave(itemId, map)) return

    const newDraftPayload = {
      ...checkinModel.value
    }
    if (draftPayload && isEqual(draftPayload, newDraftPayload)) return

    draftSaved.value = false
    draftSavedDate.value = null

    if (isEmptyDraft(newDraftPayload)) {
      draftPayload = null
      removeDraftFromStorage(itemId)
      return
    }

    draftPayload = newDraftPayload
    map[itemId] = new Date().getTime()
    await updateStorageByKey(getCheckinDraftItemLsKey(itemId), {
      ...newDraftPayload,
      createDate: new Date().getTime()
    })
    await updateStorageByKey(CHECKIN_DRAFT_MAP_LS_KEY, map)

    draftSaved.value = true
  } catch (error) {
    console.error('Error saving draft:', error)
  }
}

const initFromDraft = () => {
  try {
    const draft = readFromStorageByKey(
      getCheckinDraftItemLsKey(props.okrElement[OKR_ELEMENT_KEYS.ID])
    )

    if (draft) {
      const { createDate, ...restDraft } = draft
      draftPayload = restDraft

      checkinModel.value[COMMENT] = restDraft[COMMENT] ?? ''
      draftSavedDate.value = createDate
      draftSaved.value = true

      return true
    } else {
      return false
    }
  } catch (error) {
    console.error('Error initializing draft:', error)
    return false
  }
}

let commentWatcher = null
const initCommentWatcher = () => {
  commentWatcher = watch(
    () => checkinModel.value[COMMENT],
    () => {
      setDraft()
      initDraftWatcher()
    },
    {
      once: true
    }
  )
}

const clearCommentWatcher = () => {
  if (commentWatcher) {
    commentWatcher()
    commentWatcher = null
  }
}

const draftSavedLabel = computed(() => {
  if (!draftSavedDate.value) return t('checkin.comment_drafted')

  if (!dayjs(draftSavedDate.value).isValid()) return t('checkin.comment_draft')

  return t('checkin.comment_drafted_from', {
    date: formatDate(draftSavedDate.value)
  })
})

// const initDescriptionFieldFromDraft = computed(
//   () => draftSaved.value && Boolean(draftSavedDate.value)
// )
//#endregion DRAFT SCOPE
</script>

<template>
  <DefineTemplate>
    <CheckinPreview
      :data="checkinsForPreview"
      :loading="latestCheckinsListState[IS_LOADING]"
      :okr-element
      :table-actions-append-to="tableActionsAppendToSelectors.selector"
      @update-reactions="onUpdateReactions"
      @update-replies="
        updateCheckinReplies({
          ...$event,
          checkinsList: latestCheckinsListState[ITEMS]
        })
      "
    />
  </DefineTemplate>

  <AppDialog
    :scrollable-wrapper="false"
    :show="isOpen"
    :size="APP_MODAL_SIZES.AUTO"
    class="chm-CheckinModal"
    hide-footer
    hide-header
    hide-hero
    @on-close="onClose"
    @before-enter="onBeforeEnter"
    @after-leave="onAfterLeave"
  >
    <div :id="tableActionsAppendToSelectors.attribute" class="chm-Content">
      <div class="chm-Content_Header">
        <AppTitle :level="5" disable-margin>
          {{ modalTitle }}
        </AppTitle>

        <div v-if="!isEmpty(latestCheckinsListState[ITEMS])" class="chm-PreviewNav">
          <div v-if="checkinModel[IS_STARTED]" :id="`chpd-${checkinPreviewUid}`">
            <AppButton
              v-if="checkinModel[IS_STARTED]"
              :class="{
                'chm-PreviewToggle-collapsed': !isShowCheckinPreviewDropdown
              }"
              :data-auto-testid="SHOW_LATEST_CHECKIN_TRIGGER_BUTTON_TEST_ID"
              :data-testid="SHOW_LATEST_CHECKIN_TRIGGER_BUTTON_TEST_ID"
              class="chm-PreviewToggle"
              icon="arrow-down-black"
              type="secondary"
              @click="onShowCheckinPreviewButtonClick"
            >
              {{
                t('common.show_entity', {
                  entity: t('checkin.latest_checkin').toLowerCase()
                })
              }}
            </AppButton>

            <AppDroplist
              v-if="checkinPreviewDropdownInitialised"
              :model-value="isShowCheckinPreviewDropdown"
              :offset="[0, 8]"
              :to-selector="`#chpd-${checkinPreviewUid}`"
              append-to=".chm-CheckinModal"
              fit-content-width
              max-width="unset"
              position="bottom-start"
              show-on-init
              trigger="manual"
              @hidden="isShowCheckinPreviewDropdown = false"
            >
              <div class="chm-PreviewWrapper">
                <div class="chm-PreviewWrapper_Content">
                  <ReuseTemplate :show-actions="false" tracking-source="new checkin modal" />
                </div>
              </div>
            </AppDroplist>
          </div>

          <AppButton
            v-for="button in latestCheckinNavigation"
            :key="button.id"
            :class="[button.class]"
            :data-auto-testid="button.testId"
            :data-testid="button.testId"
            :icon="button.icon"
            class="chm-PreviewNav_Button"
            remove-padding
            type="tertiary-next"
          />
        </div>

        <div v-if="isProgressLoading || isCreateOrUpdateCheckinLoading" class="chm-Loader" />
      </div>

      <div class="chm-Content_Element">
        <OkrIcon :objective="okrElement" />
        <TitleItems
          :id-as-link="false"
          :objective="okrElement"
          id-size="small"
          style="--id-font-weight: 600"
          with-tooltip
        />
      </div>

      <div class="chm-Content_Body">
        <ReuseTemplate
          v-if="isLaunchAsAnyPreviewMode && !checkinModel[IS_STARTED]"
          :initial-slide-index="latestCheckinsListState[INITIAL_SLIDE_INDEX]"
          :show-actions="!isLaunchAsPreviewOnly"
          show-replies
          @init="resetInitialSlideIndex"
          @load-more="loadMoreCheckins"
          @edit-checkin="startEditCheckin"
        />

        <CheckinForm
          v-if="checkinModel[IS_STARTED]"
          v-model="checkinModel"
          v-model:is-comment-too-long="isCommentTooLong"
          v-model:is-progress-loading="isProgressLoading"
          :is-edit
          :is-launch-as-reopen
          :is-show-close-nested-elements-checkbox="isShowCloseNestedElementsCheckbox"
          :okr-element
          :override-current-value="isEdit ? null : overrideCurrentValue"
          :preset-backdate
          :table-actions-append-to="tableActionsAppendToSelectors.selector"
          init-description-field
          @confidence-level-id-updated="onConfidenceLevelIdUpdated"
        />
      </div>

      <div class="chm-Content_Footer">
        <div v-if="draftSaved" class="chm-ContentFooterDraft">
          <AppIcon height="20" icon-name="check-outline" width="20" />
          {{ draftSavedLabel }}
        </div>
        <ModalFooterActions
          :confirm-button-tooltip="confirmButtonTooltip"
          :disabled="isConfirmButtonDisabled"
          :loading="
            isCreateOrUpdateCheckinLoading ||
            latestCheckinsListState[IS_LOADING] ||
            isProgressLoading
          "
          :show-confirm-button="!isLaunchAsPreviewOnly"
          hide-checkbox
          @close="onCancel"
          @create="onConfirm"
        >
          <template #confirm-button-text>
            {{ confirmButtonText }}
          </template>
        </ModalFooterActions>
      </div>
    </div>
  </AppDialog>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';
@import '~@/assets/styles/interval-modal';

.chm-CheckinModal {
  --dialog-content-padding-left: 0;
  --dialog-content-padding-right: 0;
  --dialog-content-wrapper-padding: 0;
  --app-dialog-backdrop-color: hsla(
    from var(--grey-1-next) calc(h - 217.5) calc(s - 12.12) calc(l - 1.18) / 40%
  );
  --app-dialog-box-shadow: #{$secondary-box-shadow};

  --layout-padding-block: 20px;
  --layout-padding-inline: 20px;
  --checkin-field-width: 535px;
  --checkin-layout-body-gap: 4px;
  --fields-padding-left: 32px;
  --app-droplist-box-shadow: var(--app-dialog-box-shadow);
}

.chm-Content {
  max-height: calc(100vh - calc(var(--app-dialog-my) * 2));

  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.chm-Content_Header,
.chm-Content_Element {
  min-height: 64px;
  padding: 16px var(--layout-padding-inline);
  display: flex;
  align-items: center;
  box-shadow: inset 0 -1px 0 $grey-3-next;
  position: relative;
  flex-shrink: 0;
}

.chm-Content_Header {
  justify-content: space-between;
  gap: 16px;
}

.chm-Content_Element {
  gap: 6px;
  max-width: calc(
    var(--checkin-field-width) + var(--fields-padding-left) + var(--layout-padding-inline) * 2
  );
}

.chm-Content_Body,
.chm-PreviewWrapper_Content {
  padding: var(--layout-padding-inline);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  @include styled-native-scrollbar();
}

.chm-PreviewWrapper {
  overflow: hidden;
  border-radius: inherit;
}

.chm-PreviewWrapper_Content {
  max-height: min(800px, 70vh);
}

.chm-Progress {
  --value-font-size: #{$fs-14};
  --suffix-font-size: #{$fs-14};
  --suffix-font-weight: #{fw('bold')};
  --value-max-width: 10ch;
  --value-font-weight: #{fw('bold')};
  --value-line-height: 20px;
}

.chm-CommentField {
  width: 100%;
}

.chm-BodyFields_Comment {
  --placeholder-padding: 0;
  --description-field-margin-bottom: 0;
  --editor-active-outer-padding: 0;
  --placeholder-hover-color: none;

  &-active {
    --field-padding: 8px;
    --editor-active-border: none;
    // --field-border: none;
    // --field-border-radius: 0;
  }
}

.chm-CommentPlaceholder {
  color: $dark-1;
  font-size: $fs-14;
  line-height: 16px;
  font-weight: fw('semi-bold');
}

.chm-Loader {
  width: 100%;
  height: 1px;
  overflow: hidden;
  position: absolute;
  left: 0;
  bottom: 0;

  &:before {
    content: '';
    position: absolute;
    left: -50%;
    height: 100%;
    width: 40%;
    background-color: $primary-color-next;
    animation: lineAnim 1s linear infinite;
  }
}

@keyframes lineAnim {
  0% {
    left: -40%;
  }
  50% {
    left: 20%;
    width: 80%;
  }
  100% {
    left: 100%;
    width: 100%;
  }
}

.chm-Datepicker {
  @extend %interval-datepicker-styles;
  width: auto;

  :deep(.mx-icon-calendar) {
    right: 0;
  }
}

.chm-Datepicker_Input {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: fw('semi-bold');
  --datepicker-input-padding: 0px 24px 0px 0px;
  border: none;
  height: 32px;
}

.chm-Datepicker_Input,
.chm-Datepicker_Chevron {
  color: $primary-color-next;

  .chm-Datepicker-disabled & {
    cursor: not-allowed;
    color: $grey-1-next;
  }
}

.chm-Content_Footer {
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 $grey-3-next;
  --padding: var(--layout-padding-inline);
}

.chm-PreviewNav {
  display: flex;
  gap: 4px;
}

.chm-PreviewNav_Button {
  background: $grey-3-next;

  font-weight: fw('bold');

  &:not(&.swiper-button-disabled) {
    @include hoverState($grey-3-next, 1%);
    @include activeState($grey-3-next, 5%);
  }

  &.swiper-button-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.chm-PreviewToggle {
  --before-icon-transition: transform #{$transition-normal};

  &:not(&-collapsed) {
    --before-icon-transform: rotate(180deg);
  }
}

.chm-ConfirmDeleteCheckinEntity {
  font-weight: fw('semi-bold');
  white-space: normal;
}

.chm-ContentFooterDraft {
  color: $dark-3;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: $fs-12;
  font-weight: fw('regular');
  padding-left: 20px;
}
</style>
